import { devLog } from '../../utils/devLog';
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Critical Notification Modal
 * 
 * Blocking modal for critical-severity notifications.
 * Requires explicit acknowledgment - cannot dismiss via ESC or backdrop click.
 * App interaction is blocked until acknowledged.
 */
const CriticalModal = ({ notification, onAcknowledge }) => {
  devLog('[CRITICAL-MODAL] RENDER', {
    hasNotification: !!notification,
    notificationId: notification?.id,
    title: notification?.title?.substring(0, 30),
    acknowledged: notification?.acknowledged,
    documentBody: !!document.body
  });

  if (!notification) {
    devLog('[CRITICAL-MODAL] No notification, returning null');
    return null;
  }

  const handleAcknowledge = () => {
    devLog('[CRITICAL-MODAL] Acknowledged:', notification.id);
    if (onAcknowledge) {
      onAcknowledge(notification.id);
    }
  };

  devLog('[CRITICAL-MODAL] RENDERING MODAL', {
    id: notification.id,
    title: notification.title,
    open: true,
    disableEscapeKeyDown: true
  });

  return (
    <Dialog
      open={true}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        devLog('[CRITICAL-MODAL] onClose called', { reason });
        // Prevent closing via backdrop click
        if (reason === 'backdropClick') {
          devLog('[CRITICAL-MODAL] Blocked backdrop click');
          return;
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: '4px solid',
          borderColor: 'error.main',
          zIndex: 1400 // Above banner
        },
        'data-testid': 'critical-modal',
        'data-notification-id': notification.id
      }}
      BackdropProps={{
        sx: {
          zIndex: 1399 // Just below modal
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'error.light',
          color: 'error.contrastText'
        }}
      >
        <ErrorOutlineIcon />
        <Box>CRITICAL ALERT</Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          This is a critical notification that requires your acknowledgment.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Source
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {notification.source} / {notification.type}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Message
          </Typography>
          <Typography variant="body1">
            {notification.message}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Time
          </Typography>
          <Typography variant="body2">
            {new Date(notification.timestamp).toLocaleString()}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> You must acknowledge this alert to continue.
            This notification will remain in your notification center for review.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Button
          onClick={handleAcknowledge}
          variant="contained"
          color="error"
          size="large"
          fullWidth
          autoFocus
        >
          Acknowledge Critical Alert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CriticalModal;
